import React, { FunctionComponent, useRef } from "react";
import { useIntl } from "react-intl";
import useHeaderMenuActive from "@Hooks/header/useHeaderMenuActive";
import useHeaderMenuPositionTop from "@Hooks/header/useHeaderMenuPositionTop";
import { ReactComponent as AccountIcon } from "@Icons/account.svg";
import { ReactComponent as CartIcon } from "@Icons/cart.svg";
import { ReactComponent as CheerzLiveIcon } from "@Icons/cheerz-live.svg";
import { ReactComponent as GiftIcon } from "@Icons/gift.svg";
import { ReactComponent as HeartIcon } from "@Icons/heart.svg";
import cheerzLogo from "@Images/logo-cheerz.png";
import { HeaderMenuLink } from "@Types/header";
import { cartLink, cheerzLiveLink, draftsLink, giftLink } from "@Utils/links";
import HeaderSideLinkMenu from "./HeaderSideLinkMenu";
import "./style.scss";

interface Props {
  accountLinks: HeaderMenuLink[];
  cartArticlesLength: number;
  draftsNumber: number;
  login: boolean;
}

const HeaderMenu: FunctionComponent<Props> = ({
  accountLinks,
  cartArticlesLength,
  draftsNumber,
  login,
}) => {
  const itemRef = useRef<HTMLDivElement>(null);
  const intl = useIntl();
  const localeSlug = intl.locale;

  const { active, handleMouseEnter, handleMouseLeave, handleMouseLeaveMenu } =
    useHeaderMenuActive(itemRef);

  const arrowHeight = 10;
  const top = useHeaderMenuPositionTop(itemRef) - arrowHeight;

  return (
    <div className="HeaderMenu">
      <div className="HeaderMenu-left">
        <a className="HeaderMenu-item" href={`/${localeSlug}${giftLink}`}>
          <div className="HeaderMenu-item-icon">
            <GiftIcon />
          </div>
          <div className="HeaderMenu-item-title">
            {intl.formatMessage({ id: "cheerz_Header_giftIdeas" })}
          </div>
        </a>
        {localeSlug === "fr" && (
          <a className="HeaderMenu-item" href={`/${localeSlug}${cheerzLiveLink}`}>
            <div className="HeaderMenu-item-icon">
              <CheerzLiveIcon />
            </div>
            <div className="HeaderMenu-item-title">
              {intl.formatMessage({ id: "cheerz_Header_photobooth" })}
            </div>
          </a>
        )}
      </div>
      <a className="HeaderMenu-center" href={`/${localeSlug}/`}>
        <div className="HeaderMenu-logo">
          <img src={cheerzLogo} alt="Cheerz" />
        </div>
      </a>
      <div className="HeaderMenu-right">
        <div
          className="HeaderMenu-item HeaderMenu-account"
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
          ref={itemRef}
        >
          <div className="HeaderMenu-item-icon">
            <AccountIcon />
          </div>
          <div className="HeaderMenu-item-title">
            {intl.formatMessage({ id: "cheerz_Header_myAccount" })}
          </div>
          {accountLinks.length > 0 && (
            <HeaderSideLinkMenu
              links={accountLinks}
              active={active}
              login={login}
              onMouseLeave={handleMouseLeaveMenu}
              top={top}
            />
          )}
        </div>
        <a
          rel="noopener noreferrer"
          className="HeaderMenu-item"
          href={`/${localeSlug}${draftsLink}`}
        >
          <div className="HeaderMenu-item-icon">
            <HeartIcon height="24" width="24" />
            {draftsNumber > 0 && <div className="HeaderMenu-notification">{draftsNumber}</div>}
          </div>
          <div className="HeaderMenu-item-title">
            {intl.formatMessage({ id: "cheerz_Header_drafts" })}
          </div>
        </a>
        <a className="HeaderMenu-item HeaderMenu-cart" href={`/${localeSlug}${cartLink}`}>
          <div className="HeaderMenu-item-icon">
            <CartIcon />
            {cartArticlesLength > 0 && (
              <div className="HeaderMenu-notification">{cartArticlesLength}</div>
            )}
          </div>
          <div className="HeaderMenu-item-title">
            {intl.formatMessage({ id: "cheerz_Header_cart" })}
          </div>
        </a>
      </div>
    </div>
  );
};

export default HeaderMenu;
